/**
 * @prettier
 * @author Alexander Nagelberg
 *
 **/
import React, { useEffect } from 'react'
import { Field, reduxForm, formValues } from 'redux-form'
import LgyInputField from 'app/components/util/lgy-input-field/LgyInputField'
import LgyPhoneInputField from 'app/components/util/lgy-input-field/LgyInputField'
import LgySelectField from 'app/components/util/lgy-select-field/LgySelectField'
import LgyFormSubmit from 'app/components/util/lgy-form-submit/LgyFormSubmit'
import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'
import lgySelectFieldNormalizer from 'app/components/util/lgy-select-field/lgySelectFieldNormalizer'
import LgyCheckbox from 'app/components/util/lgy-checkbox/LgyCheckbox'

import { validateZip, startsWithALetter, email, phoneNumber } from 'app/utils/validations'
import { formatAllCaps, formatDollars, formatPhone, parsePhone } from 'app/utils/fieldUtils'
import { refundStatuses } from '../utils/refundUtils'

const UneditableField = ({ label, input, id }) => (
  <div>
    <label>{label}</label>
    <input type='text' id={id} disabled {...input} />
  </div>
)
const NameFields = () => (
  <label id='lgy-funding-fee-refund-name' className='usa-grid'>
    <div className='usa-width-one-half'>
      <Field label='First Name' name='firstName' component={UneditableField} required disabled />
    </div>
    <div className='usa-width-one-half'>
      <Field label='Last Name' name='lastName' component={UneditableField} required disabled />
    </div>
  </label>
)
const AddressFields = ({
  readOnly,
  focus,
  required,
  nameAddressLine1,
  nameAddressLine2,
  nameCity,
  nameState,
  nameZip
}) => (
  <div id='lgy-funding-fee-refund-address'>
    <div className='usa-grid'>
      <div className='usa-width-one-whole'>
        <Field
          label='Address'
          name={nameAddressLine1}
          component={!readOnly ? LgyInputField : UneditableField}
          required={required}
          autoFocus={focus}
        />
      </div>
    </div>
    <div className='usa-grid'>
      <div className='usa-width-one-whole'>
        <Field
          label='Address continued'
          name={nameAddressLine2}
          component={!readOnly ? LgyInputField : UneditableField}
        />
      </div>
    </div>
    <div className='usa-grid'>
      <div className='usa-width-one-whole'>
        <div className='usa-width-one-half'>
          <Field
            label='City'
            name={nameCity}
            component={!readOnly ? LgyInputField : UneditableField}
            required={required}
          />
        </div>
        <div className='usa-width-one-fourth'>
          <Field
            label='State'
            name={nameState}
            component={!readOnly ? LgyInputField : UneditableField}
            maxLength={2}
            format={formatAllCaps}
            validate={startsWithALetter}
            required={required}
          />
        </div>
        <div className='usa-width-one-fourth'>
          <Field
            label='Zip Code'
            name={nameZip}
            component={!readOnly ? LgyInputField : UneditableField}
            maxLength={5}
            validate={validateZip}
            required={required}
          />
        </div>
      </div>
    </div>
  </div>
)
const EmailFields = ({ readOnly, nameEmail }) => (
  <div className='usa-grid'>
    <div className='usa-width-one-whole'>
      <Field label='Email' name={nameEmail} validate={email} component={!readOnly ? LgyInputField : UneditableField} />
    </div>
  </div>
)

const PhoneFields = ({ readOnly, nameHome, nameCell }) => (
  <div className='usa-grid'>
    <div className='usa-width-one-half'>
      <Field
        label='Home Phone Number'
        name={nameHome}
        validate={phoneNumber}
        format={formatPhone}
        parse={parsePhone}
        component={!readOnly ? LgyPhoneInputField : UneditableField}
      />
    </div>
    <div className='usa-width-one-half'>
      <Field
        label='Cell Phone Number'
        name={nameCell}
        validate={phoneNumber}
        format={formatPhone}
        parse={parsePhone}
        component={!readOnly ? LgyPhoneInputField : UneditableField}
      />
    </div>
  </div>
)
const RefundRecordForm = ({
  fundingFee,
  readOnlyUser,
  paymentAddressAsContact,
  addressLine1,
  addressLine2,
  city,
  state,
  zipcode,
  paymentInformationAvailable,
  pointOfContactInformationAvailable,
  ...props
}) => {
  const readOnlyPaymentAddressAsContact = readOnlyUser || paymentInformationAvailable
  const readOnlyAddress = readOnlyUser
  const readOnlyEmail = readOnlyUser
  const readOnlyPhone = readOnlyUser
  const readOnlyPaymentAddress = readOnlyUser || paymentInformationAvailable || paymentAddressAsContact
  useEffect(() => {
    props.touch('paymentInformationAvailable')
    if (paymentAddressAsContact) {
      props.change('paymentAddressLine1', addressLine1)
      props.change('paymentAddressLine2', addressLine2)
      props.change('paymentCity', city)
      props.change('paymentState', state)
      props.change('paymentZipcode', zipcode)
    }
  })
  return (
    <div className='lgy-funding-fee-record-form'>
      {props.submitSucceeded && (
        <LgyAlertDialog type='success' heading='Success' paragraph>
          Successfully updated
        </LgyAlertDialog>
      )}
      {!pointOfContactInformationAvailable && (
        <LgyAlertDialog type='warning' heading='Warning' paragraph>
          Veteran point of contact address is NOT available from VA Profile, please validate or input the point of
          contact information below
        </LgyAlertDialog>
      )}
      <form autoComplete='off' className='usa-form' onSubmit={props.handleSubmit}>
        <table className='lgy-funding-fee-refund-record-form-table' role='presentation'>
          <tbody>
            <tr>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <label className='usa-grid'>
                  <div className='usa-width-one-half'>
                    <div className='lgy-funding-fee-refund-status'>
                      <Field
                        label='Status'
                        name='status'
                        component={readOnlyUser ? UneditableField : LgySelectField}
                        required
                      >
                        {Object.keys(refundStatuses).map(statusKey => (
                          <option value={statusKey} key={statusKey}>
                            {refundStatuses[statusKey]}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>
                  <div className='usa-width-one-half'>
                    <Field
                      label='Refund Amount'
                      name='refundAmount'
                      component={UneditableField}
                      format={formatDollars}
                      required
                      disabled
                    />
                  </div>
                </label>
                {!readOnlyUser && <LgyFormSubmit {...props} />}
              </td>

              <td className='lgy-funding-fee-refund-record-form-table-td'></td>
            </tr>
            <tr>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <h4>Veteran Point of Contact Information</h4>
                <Field name='pointOfContactInformationAvailable' component='input' type='hidden' />
              </td>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <h4>Veteran Payment Address Information</h4>
              </td>
            </tr>
            <tr>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <div className='usa-grid'>
                  <div className='usa-width-one-whole'>
                    <NameFields />
                  </div>
                </div>
              </td>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <div className='usa-grid'>
                  <div className='usa-width-one-whole'>
                    <Field
                      component={LgyCheckbox}
                      label='Veteran Payment Information Available'
                      name='paymentInformationAvailable'
                      disabled={true}
                    />
                    <Field
                      component={LgyCheckbox}
                      label='Payment address is the same as the point of contact'
                      name='paymentAddressAsContact'
                      disabled={readOnlyPaymentAddressAsContact}
                    />
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <div className='usa-grid'>
                  <div className='usa-width-one-whole'>
                    <div className='lgy-funding-fee-refund-address'>
                      <AddressFields
                        readOnly={readOnlyAddress}
                        focus={true}
                        nameAddressLine1='addressLine1'
                        nameAddressLine2='addressLine2'
                        nameCity='city'
                        nameState='state'
                        nameZip='zipcode'
                        required={true}
                      />
                    </div>
                  </div>
                </div>
              </td>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <div className='usa-grid'>
                  <div className='usa-width-one-whole'>
                    <div className='lgy-funding-fee-refund-address'>
                      <AddressFields
                        readOnly={readOnlyPaymentAddress}
                        focus={false}
                        nameAddressLine1='paymentAddressLine1'
                        nameAddressLine2='paymentAddressLine2'
                        nameCity='paymentCity'
                        nameState='paymentState'
                        nameZip='paymentZipcode'
                        required={false}
                      />
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <div className='usa-grid'>
                  <div className='usa-width-one-whole'>
                    <EmailFields readOnly={readOnlyEmail} nameEmail='email' />
                  </div>
                </div>
              </td>
              <td className='lgy-funding-fee-refund-record-form-table-td'></td>
            </tr>
            <tr>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <div className='usa-grid'>
                  <div className='usa-width-one-whole'>
                    <PhoneFields readOnly={readOnlyPhone} nameHome='homePhoneNumber' nameCell='cellPhoneNumber' />
                  </div>
                </div>
              </td>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                <div className='usa-grid'>
                  <div className='usa-width-one-whole'>
                    <Field
                      label='FAS Payment Instruction'
                      name='fasPaymentInstruction'
                      component={LgySelectField}
                      normalize={lgySelectFieldNormalizer}
                      disabled={readOnlyUser}
                    >
                      <option value='SURVIVING_SPOUSE'>Surviving Spouse</option>
                      <option value='FAS_BYPASS_BUSINESS_RULES'>Bypass FAS Business Rules</option>
                      <option value='FAS_PAY_BY_CHECK'>Bypass FAS Business Rules and Pay by Check</option>
                    </Field>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className='lgy-funding-fee-refund-record-form-table-td'></td>
              <td className='lgy-funding-fee-refund-record-form-table-td'>
                {!readOnlyUser && <LgyFormSubmit active title='Update' {...props} />}
              </td>
            </tr>
          </tbody>
        </table>
      </form>
    </div>
  )
}

/**
Validation for display warning message to field paymentInformationAvailable
*/

const warningValidation = ({
  paymentInformationAvailable,
  paymentAddressLine1,
  paymentAddressLine2,
  paymentCity,
  paymentState,
  paymentZipcode
}) => {
  const result = {}
  if (
    paymentInformationAvailable &&
    paymentAddressLine1 == null &&
    paymentAddressLine2 == null &&
    paymentCity == null &&
    paymentState == null &&
    paymentZipcode == null
  )
    result.paymentInformationAvailable = '[EFT on file for the Veteran.]'
  return result
}

export default reduxForm({
  warn: warningValidation
})(
  formValues(
    'paymentAddressAsContact',
    'addressLine1',
    'addressLine2',
    'city',
    'state',
    'zipcode',
    'paymentInformationAvailable',
    'pointOfContactInformationAvailable'
  )(RefundRecordForm)
)
